import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, Plus, Search } from 'lucide-react';
import { downloadFile } from '../../api/client';
import { extractErrorMessage } from '../../api/errors';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import {
  SubscriptionStatus,
  UserSubscription,
  userSubscriptionsExportPath,
  useUserSubscriptions,
} from '../../queries/userSubscriptions';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { CreateSubscriptionDialog } from './CreateSubscriptionDialog';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';

const INPUT = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDate = (value: string | null) => (value ? DATE_FORMATTER.format(new Date(value)) : '—');

const STATUS_CLASS: Record<string, string> = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  Cancelled: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  Expired: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  'Past Due': 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  Unpaid: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  Trialing: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  Promotional: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status] ?? 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
}

const columns: DataTableColumn<UserSubscription>[] = [
  {
    key: 'customer',
    header: 'Customer',
    priority: true,
    render: (s) => (
      <Link to={`/users/${s.user_id}?tab=subscriptions`} className="font-medium text-primary underline-offset-4 hover:underline">
        {[s.customer_first_name, s.customer_last_name].filter(Boolean).join(' ') || s.customer_email || `User ${s.user_id}`}
      </Link>
    ),
  },
  {
    key: 'package',
    header: 'Package',
    priority: true,
    render: (s) => s.package_name ?? s.package_code,
  },
  {
    key: 'status',
    header: 'Status',
    priority: true,
    render: (s) => <StatusBadge status={s.billing_status} />,
  },
  {
    key: 'cycle',
    header: 'Cycle',
    priority: false,
    render: (s) => s.billing_cycle ?? '—',
  },
  {
    key: 'start',
    header: 'Start',
    priority: false,
    render: (s) => formatDate(s.start_date),
  },
  {
    key: 'end',
    header: 'End',
    priority: false,
    render: (s) => formatDate(s.end_date),
  },
];

const PAGE_SIZE = 20;

export function SubscriptionsListPage() {
  const [status, setStatus] = useState<SubscriptionStatus | ''>('');
  const [packageCode, setPackageCode] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  // Inactive packages too: a one-off package (e.g. a Cup Week pass) is retired
  // once its sale ends, which is exactly when its buyers need finding.
  const { data: packages = [] } = useSubscriptionPackages(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Any filter/search change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [status, packageCode, debouncedSearch]);

  const filters = {
    status: status || undefined,
    packageCode: packageCode || undefined,
    search: debouncedSearch || undefined,
    // "All statuses" must include inactive rows too (otherwise the server
    // defaults to active-only), so the 700+ inactive subscriptions are reachable.
    includeInactive: !status,
  };
  const { data, isLoading } = useUserSubscriptions({ ...filters, page, limit: PAGE_SIZE });
  const pagination = data?.pagination;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Every subscription matching the current filters, not just this page.
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    const today = new Date().toISOString().slice(0, 10);
    const scope = (packageCode || 'all').replace(/[^a-zA-Z0-9_-]/g, '_');
    try {
      await downloadFile(userSubscriptionsExportPath(filters), `subscriptions_${scope}_${today}.csv`);
    } catch (err) {
      setExportError(extractErrorMessage(err, 'Failed to export subscriptions.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Subscriptions"
        description={data ? `${data.stats.active} active · ${data.stats.inactive} inactive` : 'Customer subscriptions across all packages.'}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" />
              Create manual subscription
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[16rem] sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or package…"
            className={`${INPUT} w-full pl-9`}
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as SubscriptionStatus | '')}
          className={INPUT}
          aria-label="Status"
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Past Due">Past Due</option>
          <option value="Trialing">Trialing</option>
          <option value="Expired">Expired</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Promotional">Promotional</option>
        </select>
        <select value={packageCode} onChange={(e) => setPackageCode(e.target.value)} className={INPUT} aria-label="Package">
          <option value="">All packages</option>
          {packages.map((p) => (
            <option key={p.package_code} value={p.package_code}>
              {p.is_active ? p.name : `${p.name} (inactive)`}
            </option>
          ))}
        </select>
      </div>

      {exportError && <p className="text-sm text-destructive">{exportError}</p>}

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data?.subscriptions ?? []}
            getRowKey={(s) => String(s.id)}
            emptyMessage="No subscriptions match the selected filters."
          />
          {pagination && pagination.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <span>
                Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>
                  Previous
                </Button>
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page >= pagination.pages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreate && <CreateSubscriptionDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}
